//* react
import React from 'react';

//* material-ui
import { Box, IconButton, InputAdornment, TextField, Tooltip, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import type { SxProps, Theme } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';

//* types
import { TemplateInfoProps } from 'types/templates/templates-menu';

//* Icons
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SaveIcon from '@mui/icons-material/Save';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import VideoCameraBackOutlinedIcon from '@mui/icons-material/VideoCameraBackOutlined';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

//* system
import useLocalization from 'hooks/useLocalization';
import { clickPreventDefaultAndStopPropagation } from 'utils/helpers';
import {
  getTemplateCategoryHebrewLabel,
  getTemplateCategoryListMeta,
} from 'views/messages-templates/templateListCategoryDisplay';
import { InlineSaveIndicator, useSaveFeedbackIdleScheduler } from 'ui-component/feedback/InlineSaveIndicator';
import { companySettingsFieldIconSx } from 'views/company/details/company-settings-field-icons';

/** שורת שם ראשית — גובה קבוע */
const HEBREW_NAME_LINE1_PX = 36;
/** מרווח בין שם ראשי לשורת משנה */
const SUBLINE_GAP_PX = 4;
/** שורת משנה — תמיד שמור גובה (מניעת Layout Shift) */
const SUBLINE_PX = 18;
/** גובה אזור הכותרת + משנה כבלוק אחד */
const TITLE_CLUSTER_MIN_PX = HEBREW_NAME_LINE1_PX + SUBLINE_GAP_PX + SUBLINE_PX;
/** רוחב אחיד לתא אייקון קטגוריה — ממוקם לפני חץ האקורדיון (אחד לפני אחרון בשורה) */
const TEMPLATE_CATEGORY_ICON_SLOT_PX = 36;

const EllipsisTooltipText: React.FC<{
  text: string;
  sx?: SxProps<Theme>;
  component?: React.ElementType;
}> = ({ text, sx, component = 'div' }) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = React.useState(false);

  const updateOverflow = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setOverflow(el.scrollWidth > el.clientWidth + 1);
  }, []);

  React.useLayoutEffect(() => {
    updateOverflow();
  }, [text, updateOverflow]);

  React.useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => updateOverflow());
    ro.observe(el);
    return () => ro.disconnect();
  }, [updateOverflow]);

  if (!text) return null;

  return (
    <Tooltip title={text} placement="top" disableHoverListener={!overflow}>
      <Typography
        ref={ref}
        component={component}
        noWrap
        sx={[{ width: '100%', minWidth: 0 }, ...(sx ? (Array.isArray(sx) ? sx : [sx]) : [])]}
      >
        {text}
      </Typography>
    </Tooltip>
  );
};

const TemplateInfo: React.FC<TemplateInfoProps> = ({
  originalTemplate,
  template,
  selected,
  hebrewName,
  isHebrew: _isHebrewNameFilter,
  templateHeader,
  resetTemplate,
  onSaveHebrewDisplayName,
  showSecondaryTemplateName = false,
  showMarketing,
  showService,
  compact = false,
  hideAccordionControl = false,
}) => {
  const theme = useTheme();
  const { currentLocale } = useLocalization();

  /** מידות שורה — ב־compact לפי theme.spacing (צפיפות גבוהה יותר) */
  const row = React.useMemo(() => {
    if (!compact) {
      return {
        L1: HEBREW_NAME_LINE1_PX,
        gap: SUBLINE_GAP_PX,
        sub: SUBLINE_PX,
        cluster: TITLE_CLUSTER_MIN_PX,
        iconSlot: TEMPLATE_CATEGORY_ICON_SLOT_PX,
        titlePx: 16,
        categoryIconPx: 18,
      };
    }
    const L1 = parseFloat(theme.spacing(3.5));
    const gap = parseFloat(theme.spacing(0.5));
    const sub = parseFloat(theme.spacing(2));
    return {
      L1,
      gap,
      sub,
      cluster: L1 + gap + sub,
      iconSlot: L1,
      titlePx: 14,
      categoryIconPx: 16,
    };
  }, [compact, theme]);

  const hebrewNameLine1ShellSxDynamic = React.useMemo(
    () =>
      ({
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        minHeight: row.L1,
        height: row.L1,
        boxSizing: 'border-box',
      }) as const,
    [row.L1],
  );

  const hebrewNameLine2SxDynamic = React.useMemo(
    () =>
      ({
        fontSize: compact ? 11 : 12,
        color: 'text.secondary',
        width: '100%',
        minHeight: row.sub,
        height: row.sub,
        lineHeight: `${row.sub}px`,
        display: 'block',
        margin: 0,
        padding: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        textAlign: 'start',
      }) as const,
    [compact, row.sub],
  );

  const hebrewNameEditFieldSxDynamic = React.useMemo(
    () => ({
      m: 0,
      mt: 0,
      mb: 0,
      width: '100%',
      flex: 1,
      minWidth: 0,
      verticalAlign: 'top',
      '& .MuiFormControl-root': {
        margin: 0,
        width: '100%',
      },
      '& .MuiInputBase-root': {
        fontSize: compact ? 13 : 15,
        fontWeight: 600,
        marginTop: '0 !important',
        marginBottom: '0 !important',
        minHeight: row.L1,
        height: row.L1,
        maxHeight: row.L1,
        alignItems: 'center',
      },
      '& .MuiInputBase-input': {
        padding: '0 4px 0 0',
        height: row.L1,
        maxHeight: row.L1,
        boxSizing: 'border-box',
        lineHeight: `${row.L1}px`,
      },
      '& .MuiInput-underline:before, & .MuiInput-underline:after': {
        display: 'none',
      },
    }),
    [compact, row.L1],
  );

  const { localization } = React.useMemo(
    () => ({
      localization: currentLocale.templatesMenu,
    }),
    [currentLocale],
  );

  const hebrewDisplayTrim = hebrewName?.hebrewTemaplateName?.trim() ?? '';

  const [editingHebrew, setEditingHebrew] = React.useState(false);
  const [draftHebrew, setDraftHebrew] = React.useState('');
  const [saveFeedback, setSaveFeedback] = React.useState<'loading' | 'success' | 'error' | null>(null);
  const scheduleSaveFeedbackIdle = useSaveFeedbackIdleScheduler();

  const categoryCode = String(template?.category ?? originalTemplate.category ?? '').trim();
  const { Icon: CategoryIcon, color: categoryIconColor } = getTemplateCategoryListMeta(categoryCode);
  const categoryTooltip = categoryCode
    ? getTemplateCategoryHebrewLabel(categoryCode)
    : 'קטגוריה לא מוגדרת';

  /** סינון לקטגוריה אחת בלבד — אייקון מעומעם, טולטיפ מלא */
  const singleCategoryFilterMode =
    (showMarketing && !showService) || (!showMarketing && showService);
  const categoryIconOpacity = singleCategoryFilterMode ? 0.15 : 1;

  const varsCount = originalTemplate.components?.reduce((count, component) => {
    const headerMatches =
      component?.type === 'HEADER'
        ? component.format === 'LOCATION'
          ? 4
          : ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(component.format)
            ? 1
            : 0
        : 0;

    const urlMatches =
      component?.buttons?.reduce((btnCount, btn) => {
        if (btn.type === 'URL') {
          const urlMatchCount = (btn.url?.match(/{{(\d+)}}/g) || []).length;
          return btnCount + urlMatchCount;
        }
        return btnCount;
      }, 0) || 0;

    const textMatches = (component?.text?.match(/{{(\d+)}}/g) || []).length;

    return count + headerMatches + urlMatches + textMatches;
  }, 0);

  const editBtnVisibilitySx = {
    opacity: 0,
    transition: (theme: Theme) => theme.transitions.create('opacity', { duration: theme.transitions.duration.shorter }),
    '.template-menu-card:hover &, .template-menu-card:focus-within &': { opacity: 1 },
    '&:focus-visible': { opacity: 1 },
  };

  const beginEditHebrew = React.useCallback(
    (e: React.MouseEvent) => {
      clickPreventDefaultAndStopPropagation(e);
      setDraftHebrew(hebrewDisplayTrim);
      setEditingHebrew(true);
    },
    [hebrewDisplayTrim],
  );

  const cancelEditHebrew = React.useCallback(() => {
    setEditingHebrew(false);
    setDraftHebrew('');
    setSaveFeedback(null);
  }, []);

  const handleSaveHebrewDisplayName = React.useCallback(async () => {
    if (!onSaveHebrewDisplayName) return;
    setSaveFeedback('loading');
    try {
      const ok = await onSaveHebrewDisplayName(draftHebrew);
      setSaveFeedback(ok ? 'success' : 'error');
      scheduleSaveFeedbackIdle(`template-hebrew-${originalTemplate.id ?? originalTemplate.name}`, () => {
        setSaveFeedback(null);
        if (ok) setEditingHebrew(false);
      });
    } catch {
      setSaveFeedback('error');
      scheduleSaveFeedbackIdle(`template-hebrew-err-${originalTemplate.id ?? originalTemplate.name}`, () => {
        setSaveFeedback(null);
      });
    }
  }, [
    draftHebrew,
    onSaveHebrewDisplayName,
    originalTemplate.id,
    originalTemplate.name,
    scheduleSaveFeedbackIdle,
  ]);

  /** אייקון קטגוריה — מוצג לפני חץ פתיחת האקורדיון (סוף שורת הכותרת) */
  const categoryIconRail = (
    <Tooltip title={categoryTooltip} placement="top">
      <Box
        component="span"
        aria-label={categoryTooltip}
        sx={{
          width: row.iconSlot,
          minWidth: row.iconSlot,
          flexShrink: 0,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: row.L1,
          minHeight: row.L1,
          px: 0.5,
          boxSizing: 'border-box',
          opacity: categoryIconOpacity,
          transition: (t: Theme) => t.transitions.create('opacity', { duration: t.transitions.duration.shorter }),
        }}
      >
        <CategoryIcon sx={{ fontSize: row.categoryIconPx, flexShrink: 0, color: categoryIconColor }} />
      </Box>
    </Tooltip>
  );

  const showSublineSaveBranch =
    onSaveHebrewDisplayName && (editingHebrew || hebrewDisplayTrim) && showSecondaryTemplateName;
  const showSublineLegacyBranch =
    !onSaveHebrewDisplayName && !!(hebrewName && hebrewDisplayTrim && showSecondaryTemplateName);

  /** כותרת משנה מוצגת — רק אז נשמר גובה שורה נוספת */
  const hasSubline = onSaveHebrewDisplayName ? showSublineSaveBranch : showSublineLegacyBranch;

  return (
    <Box sx={{ width: '100%' }}>
      <Grid
        container
        columnSpacing={compact ? 0.75 : 1.25}
        wrap="nowrap"
        alignItems="flex-start"
      >
        <Grid
          sx={{
            minWidth: 0,
            flex: '1 1 0%',
          }}
        >
          {onSaveHebrewDisplayName ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                justifyContent: 'flex-start',
                gap: hasSubline ? `${row.gap}px` : 0,
                width: '100%',
                minHeight: hasSubline ? row.cluster : row.L1,
                boxSizing: 'border-box',
                py: 0,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: 0.75,
                  width: '100%',
                  minHeight: row.L1,
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  {editingHebrew ? (
                    <Box sx={{ ...hebrewNameLine1ShellSxDynamic, flex: 1, minWidth: 0 }}>
                      <TextField
                        variant="standard"
                        fullWidth
                        size="small"
                        value={draftHebrew}
                        onChange={(e) => setDraftHebrew(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        placeholder={localization.hebrewNamePopover.inputTitle}
                        disabled={saveFeedback === 'loading'}
                        autoFocus
                        sx={hebrewNameEditFieldSxDynamic}
                        onKeyDown={(e: React.KeyboardEvent) => {
                          e.stopPropagation();
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            void handleSaveHebrewDisplayName();
                          }
                          if (e.key === 'Escape') {
                            e.preventDefault();
                            cancelEditHebrew();
                          }
                        }}
                        slotProps={{
                          input: {
                            disableUnderline: true,
                            endAdornment: (
                              <InputAdornment position="end" sx={{ marginInlineStart: 0.5 }}>
                                <IconButton
                                  disabled={saveFeedback === 'loading'}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void handleSaveHebrewDisplayName();
                                  }}
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    p: 0.5,
                                    flexShrink: 0,
                                  }}
                                  aria-label={localization.hebrewNamePopover.btnText}
                                >
                                  {saveFeedback != null ? (
                                    <InlineSaveIndicator status={saveFeedback} sizePx={20} />
                                  ) : (
                                    <SaveIcon sx={companySettingsFieldIconSx.save} />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                    </Box>
                  ) : (
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {hebrewDisplayTrim ? (
                        <Box sx={hebrewNameLine1ShellSxDynamic}>
                          <EllipsisTooltipText
                            text={hebrewDisplayTrim}
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              fontSize: row.titlePx,
                              fontWeight: 700,
                              textAlign: 'start',
                              color: 'text.primary',
                              lineHeight: `${row.L1}px`,
                              height: row.L1,
                            }}
                          />
                        </Box>
                      ) : (
                        <Box sx={hebrewNameLine1ShellSxDynamic}>
                          <EllipsisTooltipText
                            text={originalTemplate.name}
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              fontSize: row.titlePx,
                              fontWeight: 700,
                              textAlign: 'start',
                              color: 'text.primary',
                              lineHeight: `${row.L1}px`,
                              height: row.L1,
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  )}
                  {!editingHebrew ? (
                    <IconButton
                      className="template-name-edit-btn"
                      size="small"
                      sx={{
                        p: 0.5,
                        color: 'text.primary',
                        flexShrink: 0,
                        alignSelf: 'center',
                        ...editBtnVisibilitySx,
                      }}
                      onClick={beginEditHebrew}
                      aria-label="עריכת שם תצוגה בעברית"
                    >
                      <EditOutlinedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  ) : null}
                </Box>
              </Box>
              {showSublineSaveBranch ? (
                <EllipsisTooltipText text={originalTemplate.name} sx={hebrewNameLine2SxDynamic} />
              ) : null}
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: hasSubline ? `${row.gap}px` : 0,
                width: '100%',
                minHeight: hasSubline ? row.cluster : row.L1,
                boxSizing: 'border-box',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  width: '100%',
                  minHeight: row.L1,
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  {hebrewName ? (
                    hebrewDisplayTrim ? (
                      <Box sx={hebrewNameLine1ShellSxDynamic}>
                        <EllipsisTooltipText
                          text={hebrewDisplayTrim}
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            fontSize: row.titlePx,
                            fontWeight: 700,
                            textAlign: 'start',
                            color: 'text.primary',
                            lineHeight: `${row.L1}px`,
                            height: row.L1,
                          }}
                        />
                      </Box>
                    ) : (
                      <Box sx={hebrewNameLine1ShellSxDynamic}>
                        <EllipsisTooltipText
                          text={originalTemplate.name}
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            fontSize: row.titlePx,
                            fontWeight: 700,
                            textAlign: 'start',
                            color: 'text.primary',
                            lineHeight: `${row.L1}px`,
                            height: row.L1,
                          }}
                        />
                      </Box>
                    )
                  ) : (
                    <Box sx={hebrewNameLine1ShellSxDynamic}>
                      <EllipsisTooltipText
                        text={originalTemplate.name}
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          fontSize: row.titlePx,
                          fontWeight: 700,
                          textAlign: 'start',
                          color: 'text.primary',
                          lineHeight: `${row.L1}px`,
                          height: row.L1,
                        }}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
              {showSublineLegacyBranch ? (
                <EllipsisTooltipText text={originalTemplate.name} sx={hebrewNameLine2SxDynamic} />
              ) : null}
            </Box>
          )}
        </Grid>

        <Grid
          sx={{
            flexShrink: 0,
            marginInlineStart: 'auto',
            display: 'flex',
            alignItems: 'center',
            alignSelf: 'flex-start',
            height: row.L1,
            minHeight: row.L1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              flexWrap: 'nowrap',
              gap: 0.75,
              height: row.L1,
              minHeight: row.L1,
              '& .MuiSvgIcon-root': {
                verticalAlign: 'middle',
              },
            }}
          >
            {templateHeader?.format === 'IMAGE' && (
              <Tooltip title={localization.singleTemplate.media.image}>
                <Box sx={{ display: 'inline-flex', flexShrink: 0, alignItems: 'center' }}>
                  <ImageOutlinedIcon
                    sx={{ fontSize: 18 }}
                    titleAccess={localization.singleTemplate.media.image}
                  />
                </Box>
              </Tooltip>
            )}
            {templateHeader?.format === 'VIDEO' && (
              <Tooltip title={localization.singleTemplate.media.video}>
                <Box sx={{ display: 'inline-flex', flexShrink: 0, alignItems: 'center' }}>
                  <VideoCameraBackOutlinedIcon
                    sx={{ fontSize: 18 }}
                    titleAccess={localization.singleTemplate.media.video}
                  />
                </Box>
              </Tooltip>
            )}
            {templateHeader?.format === 'DOCUMENT' && (
              <Tooltip title={localization.singleTemplate.media.doc}>
                <Box sx={{ display: 'inline-flex', flexShrink: 0, alignItems: 'center' }}>
                  <StickyNote2OutlinedIcon
                    sx={{ fontSize: 18 }}
                    titleAccess={localization.singleTemplate.media.doc}
                  />
                </Box>
              </Tooltip>
            )}
            <Box sx={{ display: 'inline-flex', flexShrink: 0, alignItems: 'center' }}>
              {varsCount > 0 ? (
                <Tooltip title={'פרמטרים דינמיים'}>
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }} aria-label="פרמטרים דינמיים">
                    <Typography fontWeight={700} lineHeight={1} component="span" sx={{ fontSize: 14 }}>
                      {`{${varsCount}}`}
                    </Typography>
                  </Box>
                </Tooltip>
              ) : (
                <DescriptionOutlinedIcon sx={{ fontSize: 18 }} titleAccess="Text template" />
              )}
            </Box>
            {categoryIconRail}
            {!hideAccordionControl && (
            <Box
              sx={{
                display: 'inline-flex',
                flexShrink: 0,
                alignItems: 'center',
                /** קרוב יותר לקצה הכרטיס (ב־RTL — שמאלה) */
                marginInlineEnd: -1,
              }}
            >
              <IconButton
                onClick={selected ? resetTemplate : undefined}
                size="small"
                aria-expanded={selected}
                className="template-accordion-toggle-btn"
                aria-label={
                  selected
                    ? 'Collapse template details'
                    : 'Expand template details'
                }
                sx={{
                  marginInlineStart: 0.5,
                  transform: selected ? 'rotate(180deg)' : 'rotate(0)',
                  p: 0.25,
                  opacity: 0,
                  transition: (theme: Theme) =>
                    theme.transitions.create(['opacity', 'transform'], {
                      duration: theme.transitions.duration.shorter,
                    }),
                  '.template-menu-card:hover &, .template-menu-card:focus-within &': { opacity: 1 },
                  '&:focus-visible': { opacity: 1 },
                }}
              >
                <ExpandMoreIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TemplateInfo;
